import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setCsrf } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get('/auth/me');
      setCsrf(data.csrf);
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const data = await api.post('/auth/login', { email, password });
    if (data.requires_2fa) {
      return { requiresTwoFactor: true };
    }
    setCsrf(data.csrf);
    setUser(data.user);
    return data.user;
  }, []);

  const verifyTwoFactor = useCallback(async ({ code, recoveryCode }) => {
    const data = await api.post('/auth/2fa/verify-login', { code, recovery_code: recoveryCode });
    setCsrf(data.csrf);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    setUser(null);
  }, []);

  const value = { user, loading, login, verifyTwoFactor, logout, refresh, isAdmin: user?.role === 'admin' };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
